import dayjs from "../../utils/dayjs";
import { DEFAULT_US_DATE_FORMAT } from "../../utils/dateUtil";
import {
  CONTACT_ATTRIBUTES_ORDER_TEMPLATE,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_ROWS_PER_PAGE,
} from "../../constants";

export const predicateBy = (field: string) => {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aVal = a[field] as string | number;
    const bVal = b[field] as string | number;
    if (aVal > bVal) {
      return 1;
    } else if (aVal < bVal) {
      return -1;
    }
    return 0;
  };
};

export const getTemplateData = (
  name: string,
  contactAttributeOrdering: Record<string, unknown> | null | undefined,
) => {
  const template = CONTACT_ATTRIBUTES_ORDER_TEMPLATE.find(
    (template) => template.name === name,
  );
  if (!template) return undefined;
  // Return a shallow copy to avoid mutating the shared global template array.
  // contactAttributeOrdering allows admins to override the sort position of
  // attributes, but the override must not persist across calls/attributes.
  if (contactAttributeOrdering && contactAttributeOrdering[template.name]) {
    return {
      ...template,
      id: parseInt(String(contactAttributeOrdering[template.name])),
    };
  }
  return template;
};

export const getRowsPerPage = (defaultRowsPerPage?: number) => {
  if (defaultRowsPerPage) {
    return [defaultRowsPerPage].concat(DATAGRID_ROWS_PER_PAGE as number[]);
  } else {
    return [DATAGRID_DEFAULT_PAGE_SIZE].concat(
      DATAGRID_ROWS_PER_PAGE as number[],
    );
  }
};

export const audioToBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const blobToBase64 = async (blob: string) => {
  const audioBlob = await fetch(blob).then((r) => r.blob());
  const audioFile = new File([audioBlob], "record.wav", { type: "audio/wav" });
  return audioToBase64(audioFile);
};

export const getLastUpdated = (
  row: Record<string, unknown>,
  timezone: string,
) => {
  let userName: string | null,
    dateTime = null;
  const lastUpdatedBy = row?.lastUpdatedBy
    ? (row["lastUpdatedBy"] as string)
    : null;
  const lastUpdated = row?.lastUpdated
    ? dayjs.utc(row["lastUpdated"] as string, "YYYY-MM-DD hh:mm A")
    : null;
  userName = lastUpdatedBy;
  if (userName && userName.includes("@")) {
    userName = userName.split("@")[0];
  } else if (!userName) {
    userName = "system";
  }
  dateTime =
    lastUpdated && lastUpdated.isValid()
      ? lastUpdated.tz(timezone).format("YYYY-MM-DD hh:mm A")
      : "No date found";
  return `${dateTime} ${userName}`;
};

export const getDataManagementDateTime = (value: string, timezone: string) => {
  const utcValue = dayjs.utc(value, "YYYY-MM-DD hh:mm A");
  return utcValue && utcValue.isValid()
    ? utcValue.tz(timezone).format("YYYY-MM-DD hh:mm A")
    : "No date found";
};

export const getHistoryUpdatedTimezone = (
  row: Record<string, unknown>,
  timezone: string | null | undefined,
) => {
  const lastUpdatedTime = row?.timestamp
    ? dayjs.utc(row["timestamp"] as string, DEFAULT_US_DATE_FORMAT)
    : null;
  if (!lastUpdatedTime?.isValid()) {
    return "no date found";
  }
  let zoned;
  try {
    zoned = timezone ? lastUpdatedTime.tz(timezone) : lastUpdatedTime.local();
  } catch {
    zoned = lastUpdatedTime.local();
  }
  return zoned.isValid()
    ? zoned.format(DEFAULT_US_DATE_FORMAT)
    : "no date found";
};

const getMediaFileObject = (
  row: Record<string, unknown>,
  data: Record<string, unknown>[],
) => {
  const result = data.reduce(
    (acc: Record<string, unknown>[], cur: Record<string, unknown>) => {
      const languages =
        Object.keys(cur).length > 0 &&
        Object.keys(cur).filter((a: string) => a.includes("message"));

      const mediaFileObj = languages
        ? (cur[
            languages.find(
              (a: string) => a.split("_")[1] === row.languageCode,
            ) ?? ""
          ] as Record<string, unknown> | undefined)
        : undefined;

      const isExistsInAcc = acc?.find(
        (f) =>
          f.id === cur.id ||
          (f.sequenceNumber as number) == (cur.sequenceNumber as number),
      );
      if (
        mediaFileObj &&
        mediaFileObj.value === row.filePath &&
        cur.status !== "Expired" &&
        !isExistsInAcc
      ) {
        acc.push({
          ...cur,
        });
      }
      return acc;
    },
    [],
  );
  return result;
};

export const getMediaMgtToolTipMsg = (
  row: Record<string, unknown>,
  closureMsgData: Record<string, unknown>[],
  temporaryMsgData: Record<string, unknown>[],
) => {
  let result = "";
  const colsureMsgMediaIdInUse = getMediaFileObject(row, closureMsgData);
  const TemporaryMsgMediaIdInUse = getMediaFileObject(row, temporaryMsgData);
  const mediaFileInUse = [
    ...colsureMsgMediaIdInUse,
    ...TemporaryMsgMediaIdInUse,
  ];
  if (mediaFileInUse && mediaFileInUse.length > 0) {
    result = `The media file you were trying delete already in use.
    ${
      colsureMsgMediaIdInUse && colsureMsgMediaIdInUse.length > 0
        ? "Closure Messages Active/Scheduled Count: " +
          colsureMsgMediaIdInUse.length
        : ""
    }
    ${
      TemporaryMsgMediaIdInUse && TemporaryMsgMediaIdInUse.length > 0
        ? "Temporary Messages Active/Scheduled Count: " +
          TemporaryMsgMediaIdInUse.length
        : ""
    } `;
  }
  return result;
};

export const changeLastUpdatedDate = (
  data: Record<string, unknown>[],
  currentTimezone: string,
) => {
  return data?.reduce(
    (acc: Record<string, unknown>[], cur: Record<string, unknown>) => {
      if (cur.lastUpdated) {
        acc.push({
          ...cur,
          lastUpdated: dayjs(cur.lastUpdated as string)
            .tz(currentTimezone)
            .format("YYYY-MM-DD hh:mm A"),
        });
      }
      return acc;
    },
    [],
  );
};
